/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import {useAppDispatch, useAppSelector} from "../store";
import {Button, DatePicker, Drawer, Dropdown, Form, Input, InputNumber, Menu, Modal, Select, Space, Switch} from "antd";
import {useEffect, useState} from "react";
import {closeDrawer, openDrawer} from "../reducers/drawers-reducer";
import {triggerReload} from "../reducers/reloadsignal-reducer";
import {del, get, patch, post} from "../core/request";
import {IconSet} from "../components/icons";
import {CustomButton} from "../components/buttons";

import {CRDX, Customer, Device, DeviceModel, Person} from "../core/datamodel";
import {useHistory} from "react-router-dom";
import TextArea from "antd/es/input/TextArea";
import CustomSelect from "../components/select";

type Class = Device
const stack_selector = (state: any) => state.drawer.device
const uri = "devices"
const key = "device"
const title = "Gerät"

interface DrawerProps {
    size?: 'default' | 'large'
}


export function DeviceDrawer(props: DrawerProps) {
    const history = useHistory();
    const stack = useAppSelector(stack_selector)
    const drawer_stack = useAppSelector(state => state.drawer.stack)
    const dispatch = useAppDispatch();
    const [object, setObject] = useState({} as Class);
    const [form] = Form.useForm();

    const personReloadSignal = useAppSelector(state => state.reloadSignals.person)
    const [persons, setPersons] = useState([])

    const customerReloadSignal = useAppSelector(state => state.reloadSignals.customer)
    const [customers, setCustomers] = useState([])

    const deviceModelReloadSignal = useAppSelector(state => state.reloadSignals.devicemodel)
    const [deviceModels, setDeviceModels] = useState([])

    const [softwares, setSoftwares] = useState([])

    const [deviceType, setDeviceType] = useState("")
    const [permissions, setPermissions] = useState({} as any);


    const isNew = () => !(!!object?.props?.uuid)

    useEffect(() => {
        get("persons").then(
            (response) => setPersons(response.data.persons),
            (e) => console.error(e)
        )
    }, [stack, personReloadSignal])

    useEffect(() => {
        get("customers").then(
            (response) => setCustomers(response.data.customers),
            (e) => console.error(e)
        )
    }, [stack, customerReloadSignal])

    useEffect(() => {
        get("devicemodels").then(
            (response) => setDeviceModels(response.data.devicemodels),
            (e) => console.error(e)
        )
    }, [stack, deviceModelReloadSignal])

    useEffect(() => {
        get("softwares").then(
            (response) => setSoftwares(response.data.softwares),
            (e) => console.error(e)
        )
    }, [stack])

    useEffect(() => {
        const object = stack[stack.length - 1] as Class
        form.resetFields()
        form.setFieldsValue({
            ...object?.props,
            device_model: object?.props?.device_model ? object?.props?.device_model?.props?.uuid : undefined,
            customer: object?.props?.customer ? object?.props?.customer?.props?.uuid : undefined,
        })
        setObject(object)
        if (!!object?.props?.device_model?.props?.uuid) {
            let uuid = object?.props?.device_model?.props?.uuid
            //@ts-ignore
            let dm = deviceModels.filter(dm => dm.uuid === uuid)[0]?.type
            setDeviceType(dm)
        }
    }, [stack, form, deviceModels])

    useEffect(() => {
        get("devices/" + object?.props?.uuid + "/permissions").done((response) => {
                setPermissions(response.data.permissions);
            }
        )
    }, [object?.props?.uuid])


    const onFormFinish = () => {
        let values = form.getFieldsValue()
        console.debug(values);

        object.update({
            ...values,
            device_model: values.device_model ? new DeviceModel({'uuid': values.device_model}) : null,
            customer: values.customer ? new Customer({'uuid': values.customer}) : null,
        })

        let req: JQueryPromise<any>;
        if (isNew()) {
            req = post(uri, object.dump())
        } else {
            req = patch(uri + "/" + object.props.uuid, object.dump())
        }

        req.then(() => {
                dispatch(triggerReload(key))
                dispatch(closeDrawer(key))
            },
            (e) => {
                console.error(e);
            })
    }

    const onDisable = () => {
        object.update({'disabled': !object.props.disabled})
        patch(uri + "/" + object.props.uuid,
            {
                'uuid': object.props.uuid,
                'disabled': object.props.disabled
            }
        ).then(
            () => {
                dispatch(triggerReload(key))
                dispatch(closeDrawer(key))
            },
            (e) => {
                console.error(e);
            })
    }

    const onDelete = () => {
        Modal.confirm({
            title: "Eintrag wirklich gelöscht werden?",
            content: "Diese Aktion kann nicht rückgängig gemacht werden!",
            maskClosable: true,
            cancelText: 'Abbrechen',
            autoFocusButton: 'cancel',
            okText: 'Löschen',
            okType: 'danger',
            onOk: () => {
                del(uri + "/" + object.props.uuid)
                    .then(
                        () => {
                            history.goBack()
                            dispatch(triggerReload(key))
                            dispatch(closeDrawer(key))
                        },
                        (e) => {
                            console.error(e);
                        })
                return false;
            }
        })
    }

    const onDrawerClose = (e: any) => {
        if (e.type === 'click' &&
            (e.target as Element).className === "ant-drawer-mask"
        ) {
            Modal.confirm({
                title: "Fenster wirklich schließen?",
                content: "Alle Änderungen gehen verloren!",
                maskClosable: true,
                cancelText: 'Weiter bearbeiten',
                okText: 'Änderungen verwerfen',
                autoFocusButton: 'cancel',
                onOk: () => {
                    dispatch(closeDrawer(key));
                    return false;
                }
            })
        } else {
            dispatch(closeDrawer(key))
        }

    }

    const form_layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 16},
    };

    const personOptions = (
        <>
            {
                persons.map(
                    (person: { [key: string]: string }) =>
                        <Select.Option
                            key={person.uuid}
                            value={person.uuid}
                        >
                            {person.repr}
                        </Select.Option>)
            }
        </>
    )

    const customerOptions = (
        <>
            {
                customers.map(
                    (customer: { [key: string]: string }) =>
                        <Select.Option
                            key={customer.uuid}
                            value={customer.uuid}
                        >
                            {customer.repr}
                        </Select.Option>)
            }
        </>
    )

    const deviceModelOptions = (
        <>
            {
                deviceModels.map(
                    (deviceModel: { [key: string]: string }) =>
                        <Select.Option
                            key={deviceModel.uuid}
                            value={deviceModel.uuid}
                        >
                            {deviceModel.repr}
                        </Select.Option>)
            }
        </>
    )


    const form_element = (
        <Form
            form={form}
            onFinish={onFormFinish}
            {...form_layout}
            requiredMark={true}
        >
            <Form.Item label="Gerätemodell" style={{margin: 0}} required>
                <Form.Item
                    style={{display: 'inline-block', width: 'calc(100% - 32px - 8px)'}}
                    name="device_model"
                    rules={[{required: true, message: 'Bitte ein Gerätemodell wählen!'}]}
                >
                    <CustomSelect searchable placeholder="Gerätemodell wählen"
                                  loading={deviceModels.length === 0}
                                  onChange={(value: any) => {
                                      if (value === undefined) {
                                          setDeviceType("");
                                      } else {
                                          // @ts-ignore
                                          setDeviceType(deviceModels.filter(dm => dm.uuid === value)[0].type)
                                      }

                                  }}
                    >
                        {deviceModelOptions}
                    </CustomSelect>
                </Form.Item>
                <Form.Item style={{display: 'inline-block', margin: '0 0 0 8px'}} required>
                    <CustomButton mode='add'
                                  size="small" tooltip="Neues Gerätemodell hinzufügen"
                                  onClick={
                                      () => dispatch(openDrawer(
                                          {key: 'devicemodel', object: new DeviceModel({})}
                                      ))
                                  }/>
                </Form.Item>
            </Form.Item>

            <Form.Item label="Kunde" style={{margin: 0}} required>
                <Form.Item
                    style={{display: 'inline-block', width: 'calc(100% - 32px - 8px)'}}
                    name="customer"
                    rules={[{required: true, message: 'Bitte einem Kunden zuordnen!'}]}
                >
                    <CustomSelect searchable placeholder="Kunde wählen" loading={customers.length === 0}>
                        {customerOptions}
                    </CustomSelect>
                </Form.Item>
                <Form.Item style={{display: 'inline-block', margin: '0 0 0 8px'}}>
                    <CustomButton mode='add'
                                  size="small" tooltip="Neuen Kunden hinzufügen"
                                  onClick={
                                      () => dispatch(openDrawer(
                                          {key: 'customer', object: new Customer({})}
                                      ))
                                  }/>
                </Form.Item>
            </Form.Item>

            <Form.Item label="Geräteverantwortliche/r" style={{margin: 0}} required>
                <Form.Item
                    style={{display: 'inline-block', width: 'calc(100% - 32px - 8px)'}}
                    name="contact_person_ids"
                    rules={[{required: true, message: 'Bitte eine/n Geräteverantwortliche/n zuweisen!'}]}
                >
                    <CustomSelect searchable
                                  placeholder="Geräteverantwortliche/n wählen"
                                  mode="multiple"
                                  loading={persons.length === 0}>
                        {personOptions}
                    </CustomSelect>
                </Form.Item>
                <Form.Item style={{display: 'inline-block', margin: '0 0 0 8px'}}>
                    <CustomButton mode='add'
                                  size="small" tooltip="Neue Person hinzufügen"
                                  onClick={
                                      () => dispatch(openDrawer(
                                          {key: 'person', object: new Person({})}
                                      ))
                                  }/>
                </Form.Item>
            </Form.Item>

            <Form.Item label="Software" name="software">
                <CustomSelect searchable placeholder={"Software wählen"} allowClear>
                    {
                        softwares.map((software) => <Select.Option key={software} value={software}
                                                                   children={software}/>)
                    }
                </CustomSelect>
            </Form.Item>

            <Form.Item
                name="alias"
                label="Alias"
                tooltip="Gerätealias: wenn leer wird das Gerätemodel und der Kundenname verwendet"
            >
                <Input name='alias' placeholder="Gerätename"/>
            </Form.Item>

            <Form.Item
                label="Standort"
                tooltip="Abweichender Gerätestandort: wenn leer wird die Rechungsadresse des Kunden verwendet"
                style={{marginBottom: 0}}
            >
                <Form.Item
                    name="street"
                    style={{display: 'inline-block', width: 'calc(50% - 4px)'}}
                >
                    <Input name='street' placeholder="Straße"/>
                </Form.Item>
                <Form.Item
                    name="house_nr"
                    style={{display: 'inline-block', width: 'calc(50% - 4px)', margin: '0 0 0 8px'}}>
                    <Input name="house_nr" placeholder="Hausnummer"/>
                </Form.Item>
                <Form.Item
                    name="additional_adresse"
                    style={{display: 'inline-block', width: '100%'}}
                >
                    <Input name='additional_adresse' placeholder="Addresszusatz / Standort-Freitext"/>
                </Form.Item>
                <Form.Item
                    name="postcode"
                    style={{display: 'inline-block', width: 'calc(50% - 4px)'}}
                >
                    <Input name='postcode' placeholder="PLZ"/>
                </Form.Item>
                <Form.Item
                    name="city"
                    style={{display: 'inline-block', width: 'calc(50% - 4px)', margin: '0 0 0 8px'}}>
                    <Input name="city" placeholder="Stadt"/>
                </Form.Item>
            </Form.Item>

            <Form.Item label="Baujahr" name="manufacture_date">
                <DatePicker name="manufacture_date" picker='year' allowClear/>
            </Form.Item>

            <Form.Item label="Synology-Link" name="synologylink">
                <Input name="synologylink" placeholder="Synology-Link"/>
            </Form.Item>

            <Form.Item label="Abrechnung" name="notes_for_billing">
                <InputNumber name="notes_for_billing"
                             decimalSeparator={","}
                             min={0}
                             placeholder="Abrechnungsinformationen"
                             style={{width: '100%'}}/>
            </Form.Item>

            <Form.Item
                label="Details"
                style={{marginBottom: 0}}
                hidden={deviceType !== "CT"}
            >
                <Form.Item
                    name="number_rows"
                    style={{display: 'inline-block', width: 'calc(25% - 4px)'}}
                >
                    <InputNumber name="number_rows"
                                 decimalSeparator={","}
                                 min={0}
                                 placeholder="Zeilenzahl"
                                 style={{width: '100%'}}/>
                </Form.Item>
                <Form.Item
                    name="iterativerecon"
                    style={{display: 'inline-block', width: 'calc(75% - 4px)', margin: '0 0 0 8px'}}
                >
                    <Input name="iterativerecon" placeholder="Rekonstruktion"/>
                </Form.Item>
            </Form.Item>

            <Form.Item label="tomosynthese" name="tomosynthese" valuePropName="checked" hidden={deviceType !== "MG"}>
                <Switch/>
            </Form.Item>

            <Form.Item label="CR/DX" name="crdx" hidden={deviceType !== "XR"}>
                <Select allowClear placeholder="CR/DX">
                    {
                        Object.keys(CRDX).filter(k => typeof CRDX[k as any] === "number").map(
                            (crdx) => <Select.Option key={crdx} value={crdx} children={crdx}/>
                        )
                    }
                </Select>
            </Form.Item>

            <Form.Item label="Kommentar" name="comment">
                <TextArea allowClear name="comment" placeholder="Platz für Notizen" maxLength={2048}/>
            </Form.Item>

            {/* Buttons */}
            <Form.Item
                label=" "
                colon={false}
            >
                <Form.Item
                    style={{display: 'inline-block'}}
                >
                    <Button type="primary" htmlType="submit">
                        <IconSet type='save'/>&nbsp;Speichern
                    </Button>
                </Form.Item>
                <Form.Item
                    style={{display: 'inline-block', margin: '0 24px'}}
                >
                    <Button onClick={onDrawerClose}>
                        <IconSet type='cancel'/>&nbsp;Änderungen verwerfen
                    </Button>
                </Form.Item>
            </Form.Item>
        </Form>
    )

    const menu = (
        isNew() ? (<></>) : (
            <Dropdown
                arrow
                overlay={
                    <Menu style={{padding: 12}} items={[
                        {
                            key: "disable",
                            onClick: onDisable,
                            icon: <IconSet type='disable'/>,
                            label: !!object?.props?.disabled ? ("Reaktivieren") : ("Deaktivieren")
                        },
                        {
                            key: "delete",
                            danger: true,
                            disabled: !(object?.props?.is_deletable && permissions?.device?.delete),
                            onClick: onDelete,
                            icon: <IconSet type='delete'/>,
                            label: "Löschen"
                        },
                        {
                            key: "columnmapping",
                            onClick: () => {
                                dispatch(closeDrawer(key))
                                history.push("/devices/" + object?.props?.uuid + "/columnmapping")
                            },
                            icon: <IconSet type='columnmapping'/>,
                            label: "Spalten-Zuordnung",
                        }
                    ]}
                    />
                }
                placement="bottomRight"
                className='button-edit'
            >
                <CustomButton mode='more'/>
            </Dropdown>)
    )

    return (
        <Drawer
            open={stack.length > 0 && drawer_stack[drawer_stack.length - 1] === key}
            size={props.size}
            onClose={onDrawerClose}
            forceRender={true}
            title={
                <Space direction="horizontal" style={{width: '100%', justifyContent: 'center'}}>
                    <h1 style={{marginBottom: 0}}>{title + (isNew() ? " hinzufügen" : " bearbeiten")}</h1>
                </Space>
            }
            extra={menu}
            zIndex={drawer_stack[drawer_stack.length - 1] === key ? 1000 : 900}
        >
            {form_element}
        </Drawer>
    )
}


DeviceDrawer.defaultProps = {
    size: 'large'
}